import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { IconButton, Button, Input, DatePicker } from '@platform/components';
import { AdminMeetings } from '@platform/client';
import { AdminMeeting } from '@platform/client/schemas';
import { useMemo, useState, useContext } from 'react';
import { useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { AdminTableContext, AdminTable } from '../../AdminTable';
import { AdminListPageWrapper } from '../../AdminListPage';
import { Link } from 'wouter';
const columnHelper = createColumnHelper<AdminMeeting>();

export const MeetingsAdmin = () => {
  return (
    <AdminListPageWrapper title={'Meetings'}>
      <MeetingsAdminContent />
    </AdminListPageWrapper>
  );
};

export const MeetingsAdminContent = () => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [syncMeetingId, setSyncMeetingId] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data, isSuccess, isLoading, isError } = AdminMeetings.useGetMeetings(
    adminQueryOptionsV2,
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const { meetings = [], filteredCount = 0 } = data || {};

  const queryClient = useQueryClient();
  const refreshAttendanceMutator = AdminMeetings.useRefreshMeeting({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: AdminMeetings.getGetMeetingsQueryKey(adminQueryOptionsV2),
        });
        setSyncMeetingId('');
        setIsRefreshing(false);
      },
      onError: () => {
        alert('Error refreshing meeting, confirm valid meeting id');
        setIsRefreshing(false);
      },
    },
  });

  const refreshMeetingMutator = AdminMeetings.useRefreshMeetingsByDate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: AdminMeetings.getGetMeetingsQueryKey(adminQueryOptionsV2),
        });
        setIsRefreshing(false);
      },
      onError: () => {
        alert('Error refreshing meetings');
        setIsRefreshing(false);
      },
    },
  });

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('title', {
        header: 'Title',
        cell: ({ row }) => {
          const detailsLink = `/${row.original.zoomHost === 'EIR Call' ? 'eir' : 'community'}/${row.original.id}`;
          return (
            <div className='flex flex-col gap-1'>
              <Link href={detailsLink}>{row.original.title}</Link>
              <span className='text-xs text-gray-500'>{`(zoom id: ${row.original.zoomId})`}</span>
            </div>
          );
        },
        size: 300,
        meta: {
          filterVariant: 'text',
        },
      }),
      columnHelper.accessor('zoomHost', {
        header: 'Host',
        cell: (info) => info.getValue(),
        enableSorting: false,
        meta: {
          filterVariant: 'text',
        },
      }),
      columnHelper.accessor('joinUrl', {
        header: 'Join Url',
        cell: (joinUrl) => (
          <a className='hover:underline text-brand-500' href={joinUrl.getValue()} target='_blank'>
            {joinUrl.getValue()}
          </a>
        ),
        enableColumnFilter: false,
      }),
      columnHelper.accessor('startTime', {
        header: 'Start Time',
        cell: (startTime) => new Date(startTime.getValue()).toLocaleString(),
      }),
      columnHelper.display({
        header: 'Attended / Registered',
        cell: ({ row }) => {
          return (
            <div className='flex gap-2 justify-between items-center'>
              {`${row.original.attended} / ${row.original.registered}`}
              {row.original.registrationRequired && (
                <IconButton
                  variant='secondary-color'
                  size='sm'
                  icon='refresh'
                  isDisabled={isRefreshing}
                  onPress={() => {
                    setIsRefreshing(true);
                    refreshAttendanceMutator.mutate({
                      params: { zoom_id: row.original.zoomId },
                    });
                  }}
                ></IconButton>
              )}
            </div>
          );
        },
        size: 25,
      }),
    ] as ColumnDef<AdminMeeting>[];
  }, [refreshAttendanceMutator, isRefreshing]);

  return (
    <>
      <div className='flex gap-4 justify-end start items-end'>
        {isRefreshing && (
          <div className='mx-auto italic text-sm text-success-500'>
            Refreshing from external API, this may take a few minutes...
          </div>
        )}
        <Input
          type='inline'
          aria-label='Sync meeting by Zoom id'
          buttonText='Sync'
          placeholder='Enter zoom meeting id'
          value={syncMeetingId}
          onChange={(e) => setSyncMeetingId(e.target.value)}
          disabled={isRefreshing}
          buttonOnPress={() => {
            if (!syncMeetingId) {
              alert('Please enter a meeting id');
              return;
            }
            setIsRefreshing(true);
            const idTrimmed = syncMeetingId.replace(/\s+/g, '');
            setSyncMeetingId(idTrimmed);
            refreshAttendanceMutator.mutate({
              params: { zoom_id: idTrimmed },
            });
          }}
        />
        or
        <MeetingRefreshRange
          onSubmit={(startDate, endDate) => {
            setIsRefreshing(true);
            refreshMeetingMutator.mutate({
              data: {
                fromDate: startDate.toISOString(),
                toDate: endDate.toISOString(),
              },
            });
          }}
          isRefreshing={isRefreshing}
        />
      </div>
      <AdminTable
        data={meetings}
        columns={columns}
        filteredCount={filteredCount}
        isLoading={isLoading}
        isError={isError}
        isSuccess={isSuccess}
        enableFiltering={true}
      />
    </>
  );
};

const MeetingRefreshRange = ({
  onSubmit,
  isRefreshing,
}: {
  onSubmit: (startDate: Date, endDate: Date) => void;
  isRefreshing: boolean;
}) => {
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  return (
    <div>
      Sync meetings by date
      <div className='flex gap-4 items-end mt-1'>
        <DatePicker aria-label='From Date' onChange={(date) => setFromDate(date)} />
        to
        <DatePicker aria-label='To Date' onChange={(date) => setToDate(date)} />
        <Button
          variant='primary'
          size='lg'
          onPress={() => {
            if (fromDate && toDate) {
              onSubmit(fromDate, toDate);
            } else {
              alert('Please select both start and end dates');
            }
          }}
          isDisabled={isRefreshing}
        >
          Sync
        </Button>
      </div>
    </div>
  );
};
